import { useMemo, useRef, useState } from 'react';
import { Upload, FileText, CheckCircle2, AlertTriangle, ArrowRight, X, Sparkles } from 'lucide-react';
import { Card, SectionHeader, Badge, Button, Empty } from '@/components/ui';
import { supabase } from '@/lib/supabase';
import { slugify } from '@/lib/format';

type ParsedRow = Record<string, string>;

const TARGET_FIELDS = ['name', 'phone', 'email', 'website', 'address', 'city', 'state', 'country', 'category', 'description'];

const FIELD_ALIASES: Record<string, string[]> = {
  name: ['name', 'business', 'business_name', 'company', 'title'],
  phone: ['phone', 'telephone', 'mobile', 'contact'],
  email: ['email', 'mail', 'e-mail'],
  website: ['website', 'url', 'web', 'site'],
  address: ['address', 'street', 'street_address', 'addr'],
  city: ['city', 'town', 'locality'],
  state: ['state', 'region', 'province'],
  country: ['country'],
  category: ['category', 'industry', 'type'],
  description: ['description', 'about', 'summary', 'notes'],
};

function parseCSV(text: string): ParsedRow[] {
  const lines = text.split(/\r?\n/).filter((l) => l.trim().length > 0);
  if (lines.length === 0) return [];
  const parseLine = (line: string) => {
    const out: string[] = [];
    let cur = '';
    let inQ = false;
    for (let i = 0; i < line.length; i++) {
      const c = line[i];
      if (inQ) {
        if (c === '"' && line[i + 1] === '"') { cur += '"'; i++; }
        else if (c === '"') { inQ = false; }
        else cur += c;
      } else {
        if (c === '"') inQ = true;
        else if (c === ',') { out.push(cur); cur = ''; }
        else cur += c;
      }
    }
    out.push(cur);
    return out;
  };
  const headers = parseLine(lines[0]).map((h) => h.trim().toLowerCase());
  return lines.slice(1).map((l) => {
    const cells = parseLine(l);
    const row: ParsedRow = {};
    headers.forEach((h, i) => { row[h] = (cells[i] ?? '').trim(); });
    return row;
  });
}

function autoMap(headers: string[]): Record<string, string> {
  const map: Record<string, string> = {};
  for (const target of TARGET_FIELDS) {
    const aliases = FIELD_ALIASES[target];
    const found = headers.find((h) => aliases.includes(h.toLowerCase()));
    if (found) map[target] = found;
  }
  return map;
}

export default function BulkImport() {
  const [step, setStep] = useState<'upload' | 'map' | 'preview' | 'done'>('upload');
  const [filename, setFilename] = useState('');
  const [rows, setRows] = useState<ParsedRow[]>([]);
  const [headers, setHeaders] = useState<string[]>([]);
  const [map, setMap] = useState<Record<string, string>>({});
  const [autoPublish, setAutoPublish] = useState(false);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState<{ imported: number; duplicates: number; errors: number } | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const duplicates = useMemo(() => {
    const nameField = map.name;
    const phoneField = map.phone;
    if (!nameField && !phoneField) return new Set<number>();
    const seen = new Map<string, number>();
    const dupes = new Set<number>();
    rows.forEach((r, i) => {
      const key = [(r[nameField] ?? '').toLowerCase(), (r[phoneField] ?? '').replace(/\D/g, '')].filter(Boolean).join('|');
      if (!key) return;
      if (seen.has(key)) dupes.add(i);
      else seen.set(key, i);
    });
    return dupes;
  }, [rows, map]);

  const onFile = async (f: File) => {
    setFilename(f.name);
    const text = await f.text();
    let parsed: ParsedRow[] = [];
    if (f.name.toLowerCase().endsWith('.json')) {
      try {
        const j = JSON.parse(text);
        const arr = Array.isArray(j) ? j : Array.isArray(j.records) ? j.records : [];
        parsed = arr.map((o: Record<string, unknown>) => {
          const row: ParsedRow = {};
          Object.entries(o).forEach(([k, v]) => { row[k.toLowerCase()] = v == null ? '' : String(v); });
          return row;
        });
      } catch { parsed = []; }
    } else {
      parsed = parseCSV(text);
    }
    const hs = parsed[0] ? Object.keys(parsed[0]) : [];
    setRows(parsed);
    setHeaders(hs);
    setMap(autoMap(hs));
    setStep('map');
  };

  const doImport = async () => {
    setImporting(true);
    const toInsert = rows
      .map((r, i) => ({ r, i }))
      .filter(({ i }) => !duplicates.has(i))
      .map(({ r }) => {
        const rec: Record<string, unknown> = {};
        for (const t of TARGET_FIELDS) {
          if (map[t] && r[map[t]]) rec[t] = r[map[t]];
        }
        if (rec.name) {
          rec.slug = slugify(String(rec.name)) + '-' + Math.random().toString(36).slice(2, 8);
          rec.ai_summary = `Auto-imported ${rec.category ?? 'business'} in ${rec.city ?? 'the area'}.`;
        }
        rec.status = autoPublish ? 'published' : 'review';
        rec.pipeline_stage = autoPublish ? 'publish' : 'admin_review';
        return rec;
      })
      .filter((r) => r.name);

    const batchSize = 200;
    let imported = 0;
    let errors = 0;
    for (let i = 0; i < toInsert.length; i += batchSize) {
      const batch = toInsert.slice(i, i + batchSize);
      const { error, data } = await supabase.from('listings').insert(batch).select('id');
      if (error) errors += batch.length;
      else imported += (data ?? []).length;
    }

    await supabase.from('import_jobs').insert({
      filename,
      format: filename.toLowerCase().endsWith('.json') ? 'json' : 'csv',
      status: 'completed',
      total_rows: rows.length,
      processed_rows: rows.length,
      imported,
      duplicates: duplicates.size,
      errors,
      field_map: map,
      auto_publish: autoPublish,
      finished_at: new Date().toISOString(),
    });

    await supabase.from('notifications').insert({
      kind: 'import_completed',
      title: `${filename} imported`,
      body: `${imported} listings added · ${duplicates.size} duplicates skipped · ${errors} errors.`,
    });

    setResult({ imported, duplicates: duplicates.size, errors });
    setImporting(false);
    setStep('done');
  };

  const reset = () => {
    setStep('upload'); setFilename(''); setRows([]); setHeaders([]); setMap({}); setResult(null);
  };

  return (
    <div className="space-y-6 text-slate-800">
      <SectionHeader
        title="Bulk Import"
        subtitle="Upload CSV, Excel, JSON or XML. Fields map automatically, rows are validated, duplicates are flagged before commit, and SEO metadata is generated on publish."
      />

      <div className="grid grid-cols-4 gap-2 mb-6">
        {(['upload', 'map', 'preview', 'done'] as const).map((s, i, arr) => (
          <div key={s} className="flex items-center gap-2">
            <div className={`h-7 w-7 rounded-full grid place-items-center text-xs font-bold ${
              step === s ? 'bg-emerald-600 text-white shadow-sm' : arr.indexOf(step) > i ? 'bg-emerald-100 text-emerald-800 border border-emerald-300' : 'bg-slate-100 text-slate-500 border border-slate-200'
            }`}>{i + 1}</div>
            <div className="text-xs text-slate-700 font-bold capitalize">{s}</div>
            {i < arr.length - 1 && <ArrowRight className="h-3.5 w-3.5 text-slate-400 ml-auto" />}
          </div>
        ))}
      </div>

      {step === 'upload' && (
        <Card className="p-10">
          <div
            onClick={() => fileRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files?.[0]; if (f) void onFile(f); }}
            className="border-2 border-dashed border-slate-300 rounded-2xl p-16 text-center hover:border-emerald-600 hover:bg-slate-50 cursor-pointer transition"
          >
            <div className="mx-auto h-14 w-14 rounded-2xl bg-emerald-100 border border-emerald-200 grid place-items-center text-emerald-700 mb-4">
              <Upload className="h-6 w-6" />
            </div>
            <div className="text-slate-900 font-bold text-base">Drop a file or click to browse</div>
            <div className="text-xs text-slate-500 font-semibold mt-2">CSV · Excel (.xlsx) · JSON · XML — up to 50 MB</div>
            <input
              ref={fileRef}
              type="file"
              accept=".csv,.json,.xml,.xlsx,.xls"
              className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) void onFile(f); }}
            />
          </div>
          <div className="mt-6 text-xs text-slate-500 font-semibold">
            Try this sample CSV format:
            <pre className="mt-2 bg-slate-900 text-slate-100 rounded-xl p-3 text-[11px] font-mono border border-slate-800 overflow-x-auto">
name,phone,email,website,address,city,state,category
Blue Ridge Coffee,+1 555-321-1010,hi@blueridge.co,https://blueridge.co,101 Market St,Asheville,NC,Cafe
Harbor Point Diner,+1 555-441-9020,hello@harborpoint.co,https://harborpoint.co,52 Pier Ave,Seattle,WA,Restaurant
            </pre>
          </div>
        </Card>
      )}

      {step === 'map' && (
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="flex items-center gap-2 text-sm text-slate-900 font-bold"><FileText className="h-4 w-4 text-emerald-600" /> {filename}</div>
              <div className="text-xs text-slate-500 font-semibold">{rows.length.toLocaleString()} rows detected · {headers.length} columns</div>
            </div>
            <Button variant="ghost" size="sm" onClick={reset}><X className="h-3.5 w-3.5" /> Cancel</Button>
          </div>

          <div className="text-xs uppercase tracking-wider text-slate-400 font-bold mb-2">Field mapping</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-6">
            {TARGET_FIELDS.map((t) => (
              <div key={t} className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2">
                <div className="w-28 text-xs text-slate-700 font-bold capitalize">{t}</div>
                <select
                  value={map[t] ?? ''}
                  onChange={(e) => setMap({ ...map, [t]: e.target.value })}
                  className="flex-1 bg-white border border-slate-200 rounded-lg text-xs font-semibold text-slate-800 p-1.5 outline-none cursor-pointer"
                >
                  <option value="" className="bg-white text-slate-800">— none —</option>
                  {headers.map((h) => <option key={h} value={h} className="bg-white text-slate-800">{h}</option>)}
                </select>
                {map[t] && <CheckCircle2 className="h-4 w-4 text-emerald-600" />}
              </div>
            ))}
          </div>

          <div className="flex items-center justify-between pt-4 border-t border-slate-200">
            <label className="flex items-center gap-2 text-xs font-bold text-slate-700 cursor-pointer">
              <input type="checkbox" checked={autoPublish} onChange={(e) => setAutoPublish(e.target.checked)} className="rounded text-emerald-600 accent-emerald-600" />
              Publish automatically after import (skip admin review)
            </label>
            <Button onClick={() => setStep('preview')} disabled={!map.name}>Preview <ArrowRight className="h-3.5 w-3.5" /></Button>
          </div>
        </Card>
      )}

      {step === 'preview' && (
        <Card className="overflow-hidden">
          <div className="p-5 border-b border-slate-200 flex items-center justify-between">
            <div>
              <div className="text-sm font-bold text-slate-900">Preview & duplicate report</div>
              <div className="text-xs text-slate-500 font-semibold mt-1">
                {rows.length.toLocaleString()} rows · <span className="text-amber-600 font-bold">{duplicates.size} duplicates flagged</span> · will import {rows.length - duplicates.size}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={() => setStep('map')}>Back</Button>
              <Button onClick={doImport} disabled={importing}>
                <Sparkles className="h-3.5 w-3.5" /> {importing ? 'Importing...' : `Import ${rows.length - duplicates.size} listings`}
              </Button>
            </div>
          </div>
          <div className="overflow-x-auto max-h-[500px] scrollbar-thin">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-slate-500 font-bold uppercase tracking-wider sticky top-0 border-b border-slate-200">
                <tr>
                  <th className="text-left px-3 py-3 w-8"></th>
                  {TARGET_FIELDS.filter((f) => map[f]).map((f) => (
                    <th key={f} className="text-left px-3 py-3 capitalize">{f}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 font-semibold text-slate-700">
                {rows.slice(0, 100).map((r, i) => {
                  const isDupe = duplicates.has(i);
                  return (
                    <tr key={i} className={`hover:bg-slate-50 transition ${isDupe ? 'bg-amber-50/60' : ''}`}>
                      <td className="px-3 py-2.5">{isDupe ? <AlertTriangle className="h-3.5 w-3.5 text-amber-600" /> : <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />}</td>
                      {TARGET_FIELDS.filter((f) => map[f]).map((f) => (
                        <td key={f} className="px-3 py-2.5 text-slate-800 truncate max-w-[220px]">{r[map[f]] || <span className="text-slate-400">—</span>}</td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {step === 'done' && result && (
        <Card className="p-10 text-center">
          <div className="mx-auto h-14 w-14 rounded-2xl bg-emerald-100 border border-emerald-200 grid place-items-center text-emerald-700 mb-4">
            <CheckCircle2 className="h-7 w-7" />
          </div>
          <div className="text-xl font-bold text-slate-900">Import complete</div>
          <div className="text-xs text-slate-500 font-semibold mt-1">{filename}</div>
          <div className="mt-6 flex justify-center gap-3">
            <div className="rounded-xl bg-slate-50 border border-slate-200 px-6 py-4 min-w-32">
              <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Imported</div>
              <div className="text-2xl font-extrabold text-emerald-600 tabular-nums">{result.imported}</div>
            </div>
            <div className="rounded-xl bg-slate-50 border border-slate-200 px-6 py-4 min-w-32">
              <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Duplicates</div>
              <div className="text-2xl font-extrabold text-amber-600 tabular-nums">{result.duplicates}</div>
            </div>
            <div className="rounded-xl bg-slate-50 border border-slate-200 px-6 py-4 min-w-32">
              <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Errors</div>
              <div className="text-2xl font-extrabold text-red-600 tabular-nums">{result.errors}</div>
            </div>
          </div>
          <div className="mt-6 flex justify-center gap-2">
            <Button variant="secondary" onClick={reset}>Import another file</Button>
          </div>
          <div className="mt-4 text-xs text-slate-400 font-semibold">Sitemap, RSS and search index refresh queued.</div>
        </Card>
      )}

      {step === 'done' && !result && <Empty title="No result" />}
    </div>
  );
}
